/**
 * Time Synchronization Service
 *
 * Implements NTP-style clock synchronization using backend server time as reference.
 *
 * Key concepts:
 * - serverOffset: The difference between local clock and backend server clock
 * - timeOffset: User's manual adjustment to sync with wall clock (stored in session)
 * - getSyncedTime(): Returns the "true" time accounting for both offsets
 */

import { API_BASE_URL } from '../../../api/client';

// ============================================================================
// Types
// ============================================================================

/** Sample from time sync measurement */
interface TimeSample {
  offset: number;
  roundTripTime: number;
}

/** Callback for sync events */
type SyncCallback = (offset: number) => void;

// ============================================================================
// TimeSyncService Class
// ============================================================================

class TimeSyncService {
  /** Milliseconds difference between local and server time */
  private serverOffset: number = 0;

  /** Timestamp of last successful sync */
  private lastSyncTime: number = 0;

  /** Whether a sync is currently in progress */
  private isSyncing: boolean = false;

  /** Callbacks to notify when sync completes */
  private syncCallbacks: SyncCallback[] = [];

  /**
   * Measure the offset between local clock and backend server clock
   * Uses multiple samples to account for network latency
   *
   * @returns The calculated server offset in milliseconds
   */
  async measureServerOffset(): Promise<number> {
    if (this.isSyncing) {
      return this.serverOffset;
    }

    this.isSyncing = true;
    const samples: TimeSample[] = [];
    const numSamples = 3; // Take 3 samples to reduce network latency impact

    try {
      for (let i = 0; i < numSamples; i++) {
        const startTime = Date.now();

        // Fetch server time
        const response = await fetch(`${API_BASE_URL}/time`);
        const endTime = Date.now();
        const data = await response.json();

        const serverTime = new Date(data.timestamp).getTime();
        const roundTripTime = endTime - startTime;
        const estimatedServerTime = serverTime + roundTripTime / 2; // Account for round-trip
        const offset = estimatedServerTime - endTime;

        samples.push({
          offset,
          roundTripTime,
        });

        // Small delay between samples
        if (i < numSamples - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      // Use median offset to reduce impact of network variance
      samples.sort((a, b) => a.offset - b.offset);
      this.serverOffset = samples[Math.floor(samples.length / 2)].offset;
      this.lastSyncTime = Date.now();

      console.log('Server offset measured:', this.serverOffset, 'ms');
      console.log('Samples:', samples);

      // Notify callbacks
      this.syncCallbacks.forEach((callback) => callback(this.serverOffset));

      return this.serverOffset;
    } catch (error) {
      console.error('Error measuring server offset:', error);
      return this.serverOffset; // Return existing offset on error
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Get synchronized time in milliseconds
   * This is the "true" time according to the server
   *
   * @returns Synchronized time in milliseconds since epoch
   */
  getSyncedTimeMs(): number {
    return Date.now() + this.serverOffset;
  }

  /**
   * Get synchronized time as seconds since midnight
   *
   * @param userTimeOffset - User's manual offset in seconds
   * @returns Seconds since midnight (0-86400)
   */
  getSyncedTimeSeconds(userTimeOffset: number = 0): number {
    const syncedMs = this.getSyncedTimeMs();
    const date = new Date(syncedMs);
    // Include milliseconds for more accurate countdown calculations
    const secondsSinceMidnight =
      date.getHours() * 3600 +
      date.getMinutes() * 60 +
      date.getSeconds() +
      date.getMilliseconds() / 1000;
    return secondsSinceMidnight + userTimeOffset;
  }

  /**
   * Get a Date object representing the synced time
   *
   * @returns Synchronized Date object
   */
  getSyncedDate(): Date {
    return new Date(this.getSyncedTimeMs());
  }

  /**
   * Check if we need to re-sync (every 5 minutes)
   *
   * @returns True if sync is needed
   */
  needsSync(): boolean {
    const SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes
    return Date.now() - this.lastSyncTime > SYNC_INTERVAL;
  }

  /**
   * Register a callback to be notified when sync completes
   *
   * @param callback - Function to call with new offset
   * @returns Unsubscribe function
   */
  onSync(callback: SyncCallback): () => void {
    this.syncCallbacks.push(callback);
    return () => {
      const index = this.syncCallbacks.indexOf(callback);
      if (index > -1) {
        this.syncCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Calculate what timeOffset should be to match a target wall clock time
   * This is used when the user wants to calibrate to a wall clock
   *
   * @param wallClockTime - Time in HH:MM:SS format (24-hour)
   * @returns The timeOffset needed in seconds
   */
  calculateOffsetForWallClock(wallClockTime: string): number {
    // Convert wall clock time to seconds since midnight
    const parts = wallClockTime.split(':').map(Number);
    const hours = parts[0];
    const minutes = parts[1];
    const seconds = parts[2] || 0;
    const wallClockSeconds = hours * 3600 + minutes * 60 + seconds;

    // Get current synced time in seconds since midnight (without user offset)
    const syncedMs = this.getSyncedTimeMs();
    const date = new Date(syncedMs);
    const currentSeconds =
      date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();

    // Calculate the offset needed
    let offset = wallClockSeconds - currentSeconds;

    // Handle day wrap-around (if wall clock is earlier, assume it's tomorrow)
    if (offset < -43200) {
      // More than 12 hours behind
      offset += 86400;
    } else if (offset > 43200) {
      // More than 12 hours ahead
      offset -= 86400;
    }

    return offset;
  }

  /**
   * Initialize the time sync service
   * Should be called when app starts
   *
   * @returns The initial server offset
   */
  async initialize(): Promise<number> {
    return await this.measureServerOffset();
  }

  /**
   * Periodic sync check - call this in your app's update loop
   * Will automatically re-sync if needed
   */
  async checkAndSync(): Promise<void> {
    if (this.needsSync() && !this.isSyncing) {
      await this.measureServerOffset();
    }
  }

  /**
   * Get current server offset (for debugging/display)
   */
  getServerOffset(): number {
    return this.serverOffset;
  }

  /**
   * Get whether currently syncing
   */
  getIsSyncing(): boolean {
    return this.isSyncing;
  }

  /**
   * Get last sync time
   */
  getLastSyncTime(): number {
    return this.lastSyncTime;
  }
}

// Export singleton instance
const timeSyncService = new TimeSyncService();
export default timeSyncService;

// Export class for testing or custom instances
export { TimeSyncService };
